import { useEffect, useRef } from 'react';

/**
 * The two-pane shell shared by `/admin` and `/settings` (spec §8.1).
 *
 * Both routes are one tablist plus one tabpanel; keeping that in a single
 * component is what guarantees the keyboard contract cannot drift apart between
 * the two pages after the split. The ARIA wiring is deliberately identical to
 * the pre-Etch AdminPage (`role="tablist"` + roving tabindex + Arrow/Home/End),
 * because that behaviour was already correct and regressing it would be a step
 * backwards.
 *
 * @param {object} props
 * @param {string} props.title - visible eyebrow above the nav, also the
 *   accessible name of the tablist.
 * @param {string} props.idPrefix - namespace for the tab/panel ids, so an
 *   `/admin` panel id can never collide with a `/settings` one.
 * @param {{id: string, label: string, icon?: import('react').ElementType,
 *          group?: string, groupLabel?: string}[]} props.items
 * @param {string} props.activeId
 * @param {(id: string) => void} props.onSelect
 * @param {import('react').ReactNode} props.children - the active panel.
 */
export default function AdminShell({ title, idPrefix, items, activeId, onSelect, children }) {
  const tabRefs = useRef({});
  const pendingFocus = useRef(null);

  useEffect(() => {
    if (!pendingFocus.current) return;
    const id = pendingFocus.current;
    pendingFocus.current = null;
    tabRefs.current[id]?.focus();
  }, [activeId]);

  function handleKeyDown(event) {
    const index = items.findIndex((item) => item.id === activeId);
    if (index < 0 || items.length === 0) return;
    let next = index;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = (index + 1) % items.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = (index - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const nextId = items[next].id;
    pendingFocus.current = nextId;
    onSelect(nextId);
  }

  const nav = [];
  let previousGroup = null;
  for (const item of items) {
    if (item.groupLabel && item.group !== previousGroup) {
      nav.push(
        <p key={`group-${item.group}`} className="etch-admin__nav-heading">
          {item.groupLabel}
        </p>
      );
    }
    previousGroup = item.group;
    const Icon = item.icon;
    const selected = item.id === activeId;
    nav.push(
      <button
        key={item.id}
        type="button"
        role="tab"
        ref={(el) => {
          tabRefs.current[item.id] = el;
        }}
        id={`${idPrefix}-tab-${item.id}`}
        aria-controls={`${idPrefix}-panel-${item.id}`}
        aria-selected={selected}
        tabIndex={selected ? 0 : -1}
        className="etch-nav-item"
        onClick={() => onSelect(item.id)}
      >
        {Icon && <Icon size={16} aria-hidden="true" />}
        <span className="etch-nav-item__label">{item.label}</span>
      </button>
    );
  }

  return (
    <div className="etch-admin">
      <aside className="etch-admin__aside">
        <p className="etch-admin__title" id={`${idPrefix}-nav-title`}>
          {title}
        </p>
        <nav
          className="etch-admin__nav"
          role="tablist"
          aria-labelledby={`${idPrefix}-nav-title`}
          aria-orientation="vertical"
          onKeyDown={handleKeyDown}
        >
          {nav}
        </nav>
      </aside>

      <main
        className="etch-admin__panel etch-stack"
        data-gap="4"
        role="tabpanel"
        id={`${idPrefix}-panel-${activeId}`}
        aria-labelledby={`${idPrefix}-tab-${activeId}`}
        tabIndex={0}
      >
        {children}
      </main>
    </div>
  );
}
